import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import PageHero from '../../components/ui/PageHero';
import PageCTA from '../../components/ui/PageCTA';
import SectionHeader from '../../components/ui/SectionHeader';
import Section from '../../components/ui/Section';
import ProcessSection from '../../components/home/ProcessSection';
import { servicesList } from '../../data/services';

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled(motion.div)`
  padding: 28px 24px;
  border-radius: 16px;
  background: #fff;
  border: 1px solid #e8eef3;
  box-shadow: 0 2px 12px rgba(15, 23, 42, 0.04);
  transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s;

  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 16px 40px rgba(14, 107, 60, 0.1);
    border-color: rgba(14, 107, 60, 0.2);
  }
`;

const IconBox = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  border: 1.5px solid rgba(14, 107, 60, 0.2);
  background: rgba(14, 107, 60, 0.04);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 18px;

  i {
    font-size: 1.15rem;
    color: #0e6b3c;
  }
`;

const Title = styled.h3`
  font-family: var(--font-display);
  font-size: 1rem;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 8px;
`;

const Desc = styled.p`
  font-size: 0.86rem;
  color: #64748b;
  line-height: 1.6;
`;

const Services = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.08 });

  return (
    <div>
      <PageHero
        label="What We Do"
        title="Our Services"
        titleHighlight="Services"
        description="LMS platforms, websites, CRM, ERP, e-commerce, and custom software built to grow your business."
        breadcrumb="Services"
      />

      <Section ref={ref}>
        <div className="container">
          <SectionHeader
            label="Solutions"
            title="Everything You Need"
            subtitle="End-to-end digital services from strategy to launch and support."
          />
          <motion.div
            initial="hidden"
            animate={inView ? 'visible' : 'hidden'}
            variants={{ visible: { transition: { staggerChildren: 0.05 } } }}
          >
            <Grid>
              {servicesList.map((s) => (
                <Card
                  key={s.title}
                  as={s.link ? Link : undefined}
                  to={s.link || undefined}
                  style={s.link ? { textDecoration: 'none', color: 'inherit', cursor: 'pointer' } : undefined}
                  variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } }}
                >
                  <IconBox><i className={s.icon} /></IconBox>
                  <Title>{s.title}</Title>
                  <Desc>{s.description}</Desc>
                  {s.link && (
                    <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#0e6b3c', marginTop: 12, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      View platform <i className="fas fa-arrow-right" style={{ fontSize: '0.7rem' }} />
                    </span>
                  )}
                </Card>
              ))}
            </Grid>
          </motion.div>
        </div>
      </Section>

      <ProcessSection />

      <PageCTA
        title="Ready To Start Your Project?"
        description="Get a free consultation and custom proposal for your next build."
        primaryLabel="Get Free Consultation"
        secondaryLabel="View Portfolio"
        secondaryTo="/projects"
      />
    </div>
  );
};

export default Services;
